import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Check, AlertTriangle, Building2, Key } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline User form — used inside the Admin → Users dialog for both
 * invite-new and edit-existing flows. Extracted out of AdminPage.js as
 * part of the Feb-2026 maintainability refactor.
 */
export default function UserFormInline({ user: editUser, orgs, onSave, onCancel, saving, canAssignAllRoles, isSuperAdmin, presetOrg }) {
  const [name, setName] = useState(editUser?.name || '');
  const [email, setEmail] = useState(editUser?.email || '');
  const [role, setRole] = useState(editUser?.role || 'user');
  const [orgId, setOrgId] = useState(editUser?.org_id || presetOrg?.org_id || 'none');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const emailTimerRef = React.useRef(null);

  const checkEmailUnique = (val) => {
    setEmail(val);
    setEmailStatus(null);
    if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    if (!val.trim() || editUser) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val.trim())) return;
    setEmailStatus('checking');
    emailTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/check-email`, { params: { email: val.trim() }, withCredentials: true });
        setEmailStatus(res.data.available ? 'available' : 'taken');
      } catch { setEmailStatus(null); }
    }, 400);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (emailStatus === 'taken') return;
    onSave({ name: name.trim(), email: email.trim(), role, org_id: orgId === 'none' ? '' : orgId });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-xs">Full Name *</Label>
        <Input value={name} onChange={e => setName(e.target.value)} required className="text-sm mt-1" data-testid="user-name-input" />
      </div>
      <div>
        <Label className="text-xs">Email *</Label>
        <Input type="email" value={email} onChange={e => checkEmailUnique(e.target.value)} required disabled={!!editUser} className={`text-sm mt-1 ${emailStatus === 'taken' ? 'border-red-400 focus-visible:ring-red-400' : emailStatus === 'available' ? 'border-green-400 focus-visible:ring-green-400' : ''}`} data-testid="user-email-input" />
        {emailStatus === 'checking' && <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> Checking availability...</p>}
        {emailStatus === 'available' && <p className="text-[10px] text-green-600 mt-0.5 flex items-center gap-1"><Check className="w-2.5 h-2.5" /> Email is available</p>}
        {emailStatus === 'taken' && <p className="text-[10px] text-red-500 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> A user with this email already exists</p>}
      </div>
      <div>
        <Label className="text-xs">Role</Label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="mt-1 text-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Sales</SelectItem>
            <SelectItem value="cs">CS</SelectItem>
            {canAssignAllRoles && <SelectItem value="cro">CRO</SelectItem>}
            {canAssignAllRoles && <SelectItem value="cco">CCO</SelectItem>}
            {canAssignAllRoles && isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
          </SelectContent>
        </Select>
        {role === 'user' && (
          <p className="text-[10px] text-slate-400 mt-1" data-testid="user-role-viewonly-hint">
            View-only — sees only their organisation's estimates and cannot create or edit anything.
          </p>
        )}
      </div>
      <div>
        <Label className="text-xs">Organization</Label>
        {presetOrg && !editUser ? (
          <div className="mt-1 flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-slate-50 text-sm text-slate-600" data-testid="user-org-preset">
            <Building2 className="w-3.5 h-3.5 text-[#4ABFCF]" />
            {presetOrg.name}
          </div>
        ) : (
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="mt-1 text-sm" data-testid="user-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Organization</SelectItem>
              {orgs.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {role === 'user' && orgId === 'none' && (
          <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1" data-testid="user-org-missing-warning">
            <AlertTriangle className="w-2.5 h-2.5" /> Assign an organisation — a view-only user without one will see no estimates.
          </p>
        )}
      </div>
      {!editUser && (
        <p className="text-xs text-slate-400 flex items-center gap-1"><Key className="w-3 h-3" /> Default password: Welcome123!</p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving || !name.trim() || !email.trim() || emailStatus === 'taken' || emailStatus === 'checking'} className="bg-[#0F2040]" data-testid="user-form-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          {editUser ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
